import { createEffect, createMemo, For, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { Button } from "@openanalyst-ai/ui/button"
import { Select } from "@openanalyst-ai/ui/select"
import { Switch } from "@openanalyst-ai/ui/switch"
import { TextField } from "@openanalyst-ai/ui/text-field"
import { evaluatePluginCondition, pluginConditionAllowed } from "@/plugins/json-logic"

type WidgetFieldDeclaration = {
  key: string
  label: string
  type: "text" | "textarea" | "select" | "toggle" | "number" | "password" | "date" | "month"
  required?: boolean
  placeholder?: string
  description?: string
  sensitive?: boolean
  min?: number
  max?: number
  step?: number
  options?: Array<{ label: string; value: string }>
  visibleWhen?: unknown
  disabledWhen?: unknown
}

type FormSectionDeclaration = {
  title?: string
  description?: string
  fields: WidgetFieldDeclaration[]
  visibleWhen?: unknown
}

type SubmitActionDeclaration = {
  label?: string
  risk?: "read" | "local_write" | "external_write" | "destructive"
  visibleWhen?: unknown
  disabledWhen?: unknown
  disabledReason?: string
}

type FormWidgetData = {
  values?: Record<string, unknown>
  note?: string
}

type WidgetState = {
  submitState?: { pending: boolean; error?: string; result?: unknown }
}

const renderValue = (value: unknown) => {
  if (typeof value === "string") return value
  return JSON.stringify(value, null, 2)
}

function numericFormValue(value: string) {
  if (!value.trim()) return ""
  return value
}

function submitFormValue(field: WidgetFieldDeclaration | undefined, value: unknown) {
  if (field?.type !== "number") return value
  if (typeof value !== "string") return value
  if (!value.trim()) return ""
  const next = Number(value)
  return Number.isFinite(next) ? next : value
}

export function FormWidget(props: {
  fields?: WidgetFieldDeclaration[]
  sections?: FormSectionDeclaration[]
  data?: FormWidgetData
  state?: WidgetState
  submitLabel?: string
  submitAction?: SubmitActionDeclaration
  onSubmit: (values: Record<string, unknown>) => void
}) {
  const [values, setValues] = createStore<Record<string, unknown>>({})
  const context = createMemo(() => ({ values }))
  const allFields = createMemo(() => [
    ...(props.fields ?? []),
    ...(props.sections ?? []).flatMap((section) => section.fields),
  ])
  const fieldVisible = (field: WidgetFieldDeclaration) =>
    field.visibleWhen === undefined ||
    (pluginConditionAllowed(field.visibleWhen) && evaluatePluginCondition(field.visibleWhen, context()))
  const fieldDisabled = (field: WidgetFieldDeclaration) =>
    field.disabledWhen !== undefined &&
    (!pluginConditionAllowed(field.disabledWhen) || evaluatePluginCondition(field.disabledWhen, context()))
  const sectionVisible = (section: FormSectionDeclaration) =>
    section.visibleWhen === undefined ||
    (pluginConditionAllowed(section.visibleWhen) && evaluatePluginCondition(section.visibleWhen, context()))
  const submitVisible = createMemo(
    () =>
      props.submitAction?.visibleWhen === undefined ||
      (pluginConditionAllowed(props.submitAction.visibleWhen) &&
        evaluatePluginCondition(props.submitAction.visibleWhen, context())),
  )
  const submitDisabled = createMemo(
    () =>
      Boolean(props.state?.submitState?.pending) ||
      (props.submitAction?.disabledWhen !== undefined &&
        (!pluginConditionAllowed(props.submitAction.disabledWhen) ||
          evaluatePluginCondition(props.submitAction.disabledWhen, context()))),
  )

  createEffect(() => {
    const next = props.data?.values ?? {}
    Object.entries(next).forEach(([key, value]) => {
      if (values[key] !== undefined) return
      setValues(key, value)
    })
    allFields().forEach((field) => {
      if (values[field.key] !== undefined) return
      setValues(field.key, next[field.key] ?? (field.type === "toggle" ? false : ""))
    })
  })

  const FieldControl = (input: { field: WidgetFieldDeclaration }) => (
    <Show when={fieldVisible(input.field)}>
      <div class="flex flex-col gap-2">
        <Show
          when={
            input.field.type === "text" ||
            input.field.type === "textarea" ||
            input.field.type === "number" ||
            input.field.type === "password" ||
            input.field.type === "date" ||
            input.field.type === "month"
          }
        >
          <TextField
            type={
              input.field.type === "number" ||
              input.field.type === "password" ||
              input.field.type === "date" ||
              input.field.type === "month"
                ? input.field.type
                : undefined
            }
            label={input.field.label}
            description={input.field.description}
            placeholder={input.field.placeholder}
            multiline={input.field.type === "textarea"}
            required={input.field.required}
            min={input.field.min}
            max={input.field.max}
            step={input.field.step}
            disabled={fieldDisabled(input.field)}
            spellcheck={false}
            autocorrect="off"
            autocapitalize="off"
            value={String(values[input.field.key] ?? "")}
            onChange={(value) =>
              setValues(input.field.key, input.field.type === "number" ? numericFormValue(value) : value)
            }
          />
        </Show>
        <Show when={input.field.type === "select"}>
          <Select
            options={input.field.options ?? []}
            current={(input.field.options ?? []).find(
              (option) => option.value === String(values[input.field.key] ?? ""),
            )}
            value={(option) => option.value}
            label={(option) => option.label}
            onSelect={(option) => setValues(input.field.key, option?.value ?? "")}
            variant="secondary"
            size="small"
            placeholder={input.field.placeholder ?? "Select"}
            disabled={fieldDisabled(input.field)}
          />
        </Show>
        <Show when={input.field.type === "toggle"}>
          <div class="flex items-center justify-between rounded-lg border border-border-weak-base bg-background-base/65 px-3 py-2">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-text-strong">{input.field.label}</span>
              <Show when={input.field.description}>
                <span class="text-xs text-text-dimmed">{input.field.description}</span>
              </Show>
            </div>
            <Switch
              checked={Boolean(values[input.field.key])}
              disabled={fieldDisabled(input.field)}
              onChange={(checked) => setValues(input.field.key, checked)}
            />
          </div>
        </Show>
      </div>
    </Show>
  )

  return (
    <div class="flex flex-col gap-4">
      <div
        data-dashboard-form="panel"
        class="flex flex-col gap-4 rounded-md border border-border-weak-base/70 bg-background-secondary/55 p-3"
      >
        <Show
          when={props.sections?.length}
          fallback={
            <For each={(props.fields ?? []).filter(fieldVisible)}>{(field) => <FieldControl field={field} />}</For>
          }
        >
          <For each={(props.sections ?? []).filter(sectionVisible)}>
            {(section) => (
              <section class="flex flex-col gap-3 rounded-lg border border-border-weak-base bg-background-base/65 p-3">
                <Show when={section.title || section.description}>
                  <div class="flex flex-col gap-1">
                    <Show when={section.title}>
                      <h3 class="text-13-medium text-text-strong">{section.title}</h3>
                    </Show>
                    <Show when={section.description}>
                      <p class="text-12-regular text-text-base">{section.description}</p>
                    </Show>
                  </div>
                </Show>
                <For each={section.fields.filter(fieldVisible)}>{(field) => <FieldControl field={field} />}</For>
              </section>
            )}
          </For>
        </Show>

        <Show when={submitVisible()}>
          <Button
            variant="primary"
            disabled={submitDisabled()}
            title={submitDisabled() ? props.submitAction?.disabledReason : undefined}
            onClick={() =>
              props.onSubmit(
                Object.fromEntries(
                  Object.entries(values).map(([key, value]) => [
                    key,
                    submitFormValue(
                      allFields().find((field) => field.key === key),
                      value,
                    ),
                  ]),
                ),
              )
            }
          >
            {props.state?.submitState?.pending
              ? `${props.submitLabel ?? "Submit"}...`
              : (props.submitLabel ?? "Submit")}
          </Button>
        </Show>
      </div>

      <Show when={props.data?.note}>
        <div
          data-dashboard-form="note"
          class="rounded-md border border-border-weak-base/70 bg-background-secondary/60 px-3 py-2 text-sm text-text-base"
        >
          {props.data?.note}
        </div>
      </Show>

      <Show when={props.state?.submitState?.error || props.state?.submitState?.result}>
        <div
          data-dashboard-form={props.state?.submitState?.error ? "error" : "result"}
          class="flex flex-col gap-2 rounded-md border border-border-weak-base/70 bg-background-secondary/60 p-3"
        >
          <Show when={props.state?.submitState?.error}>
            <span class="text-sm text-text-danger">{props.state?.submitState?.error}</span>
          </Show>
          <Show when={props.state?.submitState?.result}>
            {(result) => {
              // Show a clean human message, never a raw JSON dump. Prefer a
              // note/message field; otherwise a simple Done/failed line.
              const record = (typeof result() === "object" && result() ? result() : {}) as Record<string, unknown>
              const ok = record.ok !== false
              const message =
                typeof record.note === "string" && record.note.trim()
                  ? record.note
                  : typeof record.message === "string" && record.message.trim()
                    ? record.message
                    : !ok && typeof record.error === "string"
                      ? record.error
                      : typeof result() === "string"
                        ? String(result())
                        : ok
                          ? "Done."
                          : "Something went wrong."
              return (
                <span class={`text-sm ${ok ? "text-text-base" : "text-text-danger"}`}>
                  {ok ? "✓ " : ""}
                  {message}
                </span>
              )
            }}
          </Show>
        </div>
      </Show>
    </div>
  )
}
